import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Loader2, ChevronsUpDown, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { LANGUAGES } from '@/data/languages';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

interface DobPickerProps {
  value: string; // YYYY-MM-DD
  onChange: (value: string) => void;
  minYear: number;
  maxYear: number;
  error?: string;
  id?: string;
}

function DobPicker({ value, onChange, minYear, maxYear, error, id }: DobPickerProps) {
  const parts = value ? value.split('-') : ['', '', ''];
  const [internalYear, setInternalYear] = useState(parts[0] || '');
  const [internalMonth, setInternalMonth] = useState(parts[1] || '');
  const [internalDay, setInternalDay] = useState(parts[2] || '');

  // Sync internal state when external value changes (e.g. parent clears or sets initial value)
  useEffect(() => {
    const p = value ? value.split('-') : ['', '', ''];
    setInternalYear(p[0] || '');
    setInternalMonth(p[1] || '');
    setInternalDay(p[2] || '');
  }, [value]);

  const daysInMonth = (year: string, month: string) => {
    if (!year || !month) return 31;
    return new Date(parseInt(year), parseInt(month), 0).getDate();
  };

  const update = (y: string, m: string, d: string) => {
    if (y && m && d) {
      onChange(`${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`);
    } else {
      onChange('');
    }
  };

  const maxDays = daysInMonth(internalYear, internalMonth);

  const handleDayChange = (raw: string) => {
    let d = raw.replace(/\D/g, '').slice(0, 2);
    if (d && parseInt(d) > maxDays) d = String(maxDays);
    setInternalDay(d);
    update(internalYear, internalMonth, d);
  };

  const handleMonthChange = (m: string) => {
    setInternalMonth(m);
    // Clamp the day if the new month has fewer days (e.g. 31 -> Feb)
    const md = daysInMonth(internalYear, m);
    let d = internalDay;
    if (d && parseInt(d) > md) {
      d = String(md);
      setInternalDay(d);
    }
    update(internalYear, m, d);
  };

  const handleYearChange = (raw: string) => {
    const y = raw.replace(/\D/g, '').slice(0, 4);
    setInternalYear(y);
    update(y, internalMonth, internalDay);
  };

  return (
    <div className="flex gap-2">
      <div className="flex-1">
        <Input
          id={id}
          type="number"
          inputMode="numeric"
          min={1}
          max={maxDays}
          placeholder="DD"
          aria-label="Day"
          value={internalDay}
          onChange={(e) => handleDayChange(e.target.value)}
          className={error ? 'border-red-500' : ''}
        />
      </div>
      <div className="flex-[2]">
        <Select
          value={internalMonth}
          onValueChange={handleMonthChange}
        >
          <SelectTrigger className={error ? 'border-red-500' : ''} aria-label="Month">
            <SelectValue placeholder="Month" />
          </SelectTrigger>
          <SelectContent>
            {MONTHS.map((m, i) => (
              <SelectItem key={m} value={String(i + 1).padStart(2, '0')}>{m}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex-[2]">
        <Input
          type="number"
          inputMode="numeric"
          min={minYear}
          max={maxYear}
          placeholder="YYYY"
          aria-label="Year"
          value={internalYear}
          onChange={(e) => handleYearChange(e.target.value)}
          className={error ? 'border-red-500' : ''}
        />
      </div>
    </div>
  );
}
import {
  OnboardingStudentProfileRequest,
  OnboardingParentProfileRequest,
} from '@/lib/api/auth';
import { UserProfileData } from '@/pages/OnboardingStep2Page';
import { studentProfileSchema, parentProfileSchema } from '@/lib/zod-schemas';
import { ZodError } from 'zod';

const yearGroups = [
  { value: '3', label: 'Year 3 (Age 7-8)' },
  { value: '4', label: 'Year 4 (Age 8-9)' },
  { value: '5', label: 'Year 5 (Age 9-10)' },
  { value: '6', label: 'Year 6 (Age 10-11)' },
  { value: '7', label: 'Year 7 (Age 11-12)' },
  { value: '8', label: 'Year 8 (Age 12-13)' },
  { value: '9', label: 'Year 9 (Age 13-14)' },
  { value: '10', label: 'Year 10 (Age 14-15)' },
  { value: '11', label: 'Year 11 (Age 15-16)' },
];

const curriculumTypes = [
  { value: 'uk', label: 'UK National Curriculum' },
  { value: 'ib', label: 'International Baccalaureate' },
  { value: 'us', label: 'American Curriculum' },
  { value: 'uae', label: 'UAE National Curriculum' },
  { value: 'other', label: 'Other' },
];

interface OnboardingStep2Props {
  userType: 'student' | 'parent';
  onNext: (data: UserProfileData) => Promise<void>;
  onBack: () => void;
  isLoading: boolean;
}

export default function OnboardingStep2Profile({ userType, onNext, onBack, isLoading }: OnboardingStep2Props) {
  // Student profile state
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [gender, setGender] = useState('');
  const [yearGroup, setYearGroup] = useState('');
  const [firstLanguage, setFirstLanguage] = useState('');
  const [eal, setEal] = useState(false);
  const [curriculumType, setCurriculumType] = useState<'uk' | 'ib' | 'us' | 'uae' | 'other'>('uk');
  const [speechSupport, setSpeechSupport] = useState('');

  // Parent profile state (additional fields)
  const [childFirstName, setChildFirstName] = useState('');
  const [childLastName, setChildLastName] = useState('');
  const [childDob, setChildDob] = useState('');

  const [languageOpen, setLanguageOpen] = useState(false);

  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleSubmit = async () => {
    setErrors({});

    try {
      let data: UserProfileData;
      
      if (userType === 'student') {
        const validatedData = studentProfileSchema.parse({
          date_of_birth: dateOfBirth,
          gender,
          year_group: yearGroup || undefined,
          first_language: firstLanguage,
          eal,
          speech_language_support: speechSupport,
        });

        data = {
          date_of_birth: validatedData.date_of_birth,
          gender: validatedData.gender,
          year_group: validatedData.year_group,
          first_language: validatedData.first_language,
          eal: validatedData.eal,
          speech_language_support: validatedData.speech_language_support,
        } as OnboardingStudentProfileRequest;
      } else if (userType === 'parent') {
        const validatedData = parentProfileSchema.parse({
          child_first_name: childFirstName,
          child_last_name: childLastName,
          child_date_of_birth: childDob,
          year_group: yearGroup,
          first_language: firstLanguage,
          eal,
          curriculum_type: curriculumType,
          speech_language_support: speechSupport,
        });

        data = {
          child_first_name: validatedData.child_first_name,
          child_last_name: validatedData.child_last_name,
          child_date_of_birth: validatedData.child_date_of_birth,
          year_group: validatedData.year_group,
          first_language: validatedData.first_language,
          eal: validatedData.eal,
          curriculum_type: validatedData.curriculum_type,
          speech_language_support: validatedData.speech_language_support,
        } as OnboardingParentProfileRequest;
      }

      await onNext(data);
    } catch (err) {
      if (err instanceof ZodError) {
        const fieldErrors: Record<string, string> = {};
        err.errors.forEach((issue) => {
          const field = issue.path[0] as string;
          if (!fieldErrors[field]) {
            fieldErrors[field] = issue.message;
          }
        });
        setErrors(fieldErrors);
      } else {
        setErrors({ _form: err instanceof Error ? err.message : 'An error occurred' });
      }
    }
  };

  const getTitle = () => {
    switch (userType) {
      case 'student':
        return 'Tell us about yourself';
      case 'parent':
        return "Tell us about your child";
    }
  };

  const getDescription = () => {
    switch (userType) {
      case 'student':
        return 'This helps us personalise your learning experience';
      case 'parent':
        return 'This helps us personalise the learning experience for your child';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-secondary/20 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-lg">
        <Card className="w-full shadow-xl">
          <CardHeader className="space-y-1 text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-bold">2</span>
              <span className="text-sm text-muted-foreground">of 3</span>
            </div>
            <CardTitle className="text-2xl font-bold">{getTitle()}</CardTitle>
            <CardDescription>{getDescription()}</CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-4">
            {errors._form && (
              <div className="p-3 text-sm text-red-500 bg-red-50 dark:bg-red-950/20 rounded-lg">
                {errors._form}
              </div>
            )}

            {/* Student/Parent fields */}
            {(userType === 'student' || userType === 'parent') && (
              <>
                {/* Parent-specific child info */}
                {userType === 'parent' && (
                  <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
                    <h4 className="font-medium">Child's Details</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="childFirstName">First Name</Label>
                        <Input
                          id="childFirstName"
                          value={childFirstName}
                          onChange={(e) => setChildFirstName(e.target.value)}
                          placeholder="Child's first name"
                          className={errors.child_first_name ? 'border-red-500' : ''}
                        />
                        {errors.child_first_name && <p className="text-sm text-red-500">{errors.child_first_name}</p>}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="childLastName">Last Name</Label>
                        <Input
                          id="childLastName"
                          value={childLastName}
                          onChange={(e) => setChildLastName(e.target.value)}
                          placeholder="Child's last name"
                          className={errors.child_last_name ? 'border-red-500' : ''}
                        />
                        {errors.child_last_name && <p className="text-sm text-red-500">{errors.child_last_name}</p>}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Date of Birth</Label>
                      <DobPicker
                        value={childDob}
                        onChange={setChildDob}
                        minYear={new Date().getFullYear() - 18}
                        maxYear={new Date().getFullYear() - 3}
                        error={errors.child_date_of_birth}
                      />
                      {errors.child_date_of_birth && <p className="text-sm text-red-500">{errors.child_date_of_birth}</p>}
                    </div>
                  </div>
                )}

                {/* Student Date of Birth */}
                {userType === 'student' && (
                  <div className="space-y-2">
                    <Label htmlFor="dateOfBirthDay">Date of Birth</Label>
                    <DobPicker
                      id="dateOfBirthDay"
                      value={dateOfBirth}
                      onChange={setDateOfBirth}
                      minYear={new Date().getFullYear() - 100}
                      maxYear={new Date().getFullYear() - 18}
                      error={errors.date_of_birth}
                    />
                    {errors.date_of_birth && <p className="text-sm text-red-500">{errors.date_of_birth}</p>}
                  </div>
                )}

                {/* Gender (student) */}
                {userType === 'student' && (
                  <div className="space-y-2">
                    <Label htmlFor="gender">Gender</Label>
                    <Select value={gender} onValueChange={setGender}>
                      <SelectTrigger id="gender" className={errors.gender ? 'border-red-500' : ''}>
                        <SelectValue placeholder="Select an option" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="male">Male</SelectItem>
                        <SelectItem value="female">Female</SelectItem>
                        <SelectItem value="non_binary">Non-binary</SelectItem>
                        <SelectItem value="prefer_not_to_say">Prefer not to say</SelectItem>
                      </SelectContent>
                    </Select>
                    {errors.gender && <p className="text-sm text-red-500">{errors.gender}</p>}
                  </div>
                )}

                {/* Year Group: optional for adult self-registrants, required for parent-registered children */}
                {userType === 'student' && (
                  <div className="space-y-2">
                    <Label htmlFor="yearGroup">Year Group (optional)</Label>
                    <Select value={yearGroup} onValueChange={setYearGroup}>
                      <SelectTrigger id="yearGroup">
                        <SelectValue placeholder="Select year group (if applicable)" />
                      </SelectTrigger>
                      <SelectContent>
                        {yearGroups.map((yg) => (
                          <SelectItem key={yg.value} value={yg.value}>{yg.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Year Group + Curriculum: school pupils only (parent-registered children) */}
                {userType === 'parent' && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="yearGroup">Year Group</Label>
                      <Select value={yearGroup} onValueChange={setYearGroup}>
                        <SelectTrigger id="yearGroup" className={errors.year_group ? 'border-red-500' : ''}>
                          <SelectValue placeholder="Select year group" />
                        </SelectTrigger>
                        <SelectContent>
                          {yearGroups.map((yg) => (
                            <SelectItem key={yg.value} value={yg.value}>{yg.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.year_group && <p className="text-sm text-red-500">{errors.year_group}</p>}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="curriculum">Curriculum</Label>
                      <Select value={curriculumType} onValueChange={(v) => setCurriculumType(v as typeof curriculumType)}>
                        <SelectTrigger id="curriculum">
                          <SelectValue placeholder="Select curriculum" />
                        </SelectTrigger>
                        <SelectContent>
                          {curriculumTypes.map((ct) => (
                            <SelectItem key={ct.value} value={ct.value}>{ct.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                )}

                {/* First Language (optional) */}
                <div className="space-y-2">
                  <Label>First Language (optional)</Label>
                  <Popover open={languageOpen} onOpenChange={setLanguageOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={languageOpen}
                        className="w-full justify-between font-normal"
                      >
                        {firstLanguage || "Select a language..."}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-full p-0" align="start">
                      <Command>
                        <CommandInput placeholder="Search language..." />
                        <CommandList>
                          <CommandEmpty>No language found. Type to set a custom value.</CommandEmpty>
                          <CommandGroup>
                            {LANGUAGES.map((lang) => (
                              <CommandItem
                                key={lang}
                                value={lang}
                                onSelect={(val) => {
                                  setFirstLanguage(val === firstLanguage ? '' : val);
                                  setLanguageOpen(false);
                                }}
                              >
                                <Check
                                  className={cn(
                                    "mr-2 h-4 w-4",
                                    firstLanguage === lang ? "opacity-100" : "opacity-0",
                                  )}
                                />
                                {lang}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>

                {/* EAL Checkbox */}
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="eal"
                    checked={eal}
                    onChange={(e) => setEal(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  <Label htmlFor="eal" className="font-normal">
                    English is an Additional Language (EAL)
                  </Label>
                </div>
              </>
            )}
          </CardContent>

          <CardFooter className="flex gap-4">
            <Button
              variant="outline"
              onClick={onBack}
              disabled={isLoading}
              className="flex-1"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isLoading}
              className="flex-1"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Continue'
              )}
            </Button>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}
